/* ============================================================
 * 60ft Ink — Unified order builder (dev pilot)
 * ------------------------------------------------------------
 * Replaces size-quantity-matrix.liquid for `order-builder-ready`
 * products only. Combines size/qty selection, colour, print
 * locations and live real pricing into one widget, styled like
 * ft-quote-calculator.js but driving REAL state instead of an
 * estimate.
 *
 * Deliberately scoped to standard (non-Special, non-promo-front-
 * only) products — size-quantity-matrix.liquid keeps handling
 * those, untouched. See /Users/rolandsoutar/.claude/plans/
 * humble-wiggling-peach.md for the full rationale.
 *
 * Cart/pricing logic (discountTiers, getDiscount, PRINT_VARIANTS,
 * buildBaseItems, addToCart, sendItemsToCart) is ported near-
 * verbatim from size-quantity-matrix.liquid's standard-path code,
 * since that logic is already correct and tested — only the
 * presentation layer and print-location handling are new.
 *
 * Print-location pills carry NO pricing logic of their own — they
 * toggle the REAL print-location-selector.liquid checkboxes
 * underneath (set checked + dispatch change), the exact technique
 * ft-mockup-builder.js's syncPrintLocationSelector() already uses.
 * That keeps print-location-selector.liquid as the single source
 * of truth for location pricing/upload-URL properties.
 *
 * Colour swatches keep the class name `size-qty-matrix__swatch`
 * (and `data-color` attribute) so ft-mockup-builder.js's existing
 * colour-sync (initColourSync(), delegated click listener) keeps
 * working with zero changes there.
 *
 * The Add to Cart action binds to the SAME real #pls-main-add-btn
 * element size-quantity-matrix.liquid uses — this is what the
 * mockup wizard's review-step CTA already proxies a click to, so
 * that existing chain (wizard CTA -> #pls-main-add-btn -> real
 * cart submit) keeps working with zero changes to the wizard's
 * own proxy code.
 * ============================================================ */

(function () {
  'use strict';

  var PRINT_VARIANTS = {
    front:        47537209803054,
    back:         47537209901358,
    large_front:  52020797309230,
    left_sleeve:  52394261348654,
    right_sleeve: 52394261512494
  };

  var PLS_CHECKBOX_IDS = {
    front: 'pls-front',
    large_front: 'pls-large-front',
    back: 'pls-back',
    left_sleeve: 'pls-left-sleeve',
    right_sleeve: 'pls-right-sleeve'
  };

  // Maps a print-location pill to the {side, zone} it corresponds to in
  // ft-print-zones.json — the same config/geometry ft-mockup-builder.js
  // uses for its own zone thumbnails, just looked up independently here so
  // the pills can show one without depending on that widget's (async,
  // possibly-absent-if-no-mockup-ready-tag) init.
  var LOCATION_TO_ZONE = {
    front:        { side: 'front',        zone: 'left_chest' },
    large_front:  { side: 'front',        zone: 'full_front' },
    back:         { side: 'back',         zone: 'full_back' },
    left_sleeve:  { side: 'left_sleeve',  zone: 'left_sleeve' },
    right_sleeve: { side: 'right_sleeve', zone: 'right_sleeve' }
  };

  // ---------- Live bulk pricing (2026-10-15) ----------
  // Mirrors the REAL Hulk volume-discount offers exactly, read from each
  // offer's volume-discount/function-configuration metafield on
  // 2026-10-15 and checked against real order #2455 to the cent:
  //
  //   "combined discounts" — collection offer on Printable Gear (tag
  //     `Printable`, excluding mws_* generated types). ONE pooled count
  //     across every Printable garment in the cart.
  //   "print bulk discount Front left chest", "PRint discounts full back",
  //   "Left Sleeve", "Right Sleeve", "Large Front Print" — product offers,
  //     each tiered on its OWN quantity only.
  //
  //   2026-10-15: the Large Front print product used to carry the
  //   `Printable` tag, which dropped it into the garment pool and double-
  //   counted it (10 tees + large front = 20 units = 25% off). Tag removed
  //   and it got its own Hulk offer, so it now prices like every other
  //   print location.
  //
  // All offers use the same ladder. Discount is per unit, rounded DOWN to
  // the cent (e.g. $12.05 at 18% = $2.16 off, not $2.17).
  //
  // The 2026-10-14 change replaced the live total with "Bulk savings
  // calculated in cart" because the old maths applied one % to garments
  // AND prints, which the cart doesn't do. This restores a live figure
  // that matches the cart instead. If the Hulk offers change, update
  // DISCOUNT_TIERS and the pool rules here.
  var DISCOUNT_TIERS = [
    { min: 200, pct: 35, label: '35% off' },
    { min: 100, pct: 33, label: '33% off' },
    { min: 50,  pct: 30, label: '30% off' },
    { min: 20,  pct: 25, label: '25% off' },
    { min: 10,  pct: 18, label: '18% off' }
  ];

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function getDiscount(totalQty) {
    for (var i = 0; i < DISCOUNT_TIERS.length; i++) {
      if (totalQty >= DISCOUNT_TIERS[i].min) return DISCOUNT_TIERS[i];
    }
    return null;
  }

  function getNextTier(totalQty) {
    for (var i = DISCOUNT_TIERS.length - 1; i >= 0; i--) {
      if (DISCOUNT_TIERS[i].min > totalQty) return DISCOUNT_TIERS[i];
    }
    return null;
  }

  function tierPct(qty) {
    var t = getDiscount(qty);
    return t ? t.pct : 0;
  }

  // Hulk's per-unit rounding: discount floored to the cent.
  function discountedUnit(cents, pct) {
    if (!pct) return cents;
    return cents - Math.floor((cents * pct) / 100);
  }

  /**
   * Pure pricing calculation — no DOM. Exposed on window for testing.
   *   lines:        [{ qty, price }]  garment rows on this page (price in cents)
   *   locPrices:    { loc: cents }    selected print locations only
   *   groupEligible: this page's garment is in the Printable Gear pool
   *   cart:         { group: n, prints: { loc: n } }  already in cart
   */
  function computePricing(lines, locPrices, groupEligible, cart) {
    cart = cart || { group: 0, prints: {} };
    var pageQty = 0;
    lines.forEach(function (l) { pageQty += l.qty; });

    var unitsPerShirt = groupEligible ? 1 : 0;
    var groupQty = pageQty * unitsPerShirt + (cart.group || 0);
    var groupPct = tierPct(groupQty);

    var full = 0;
    var final = 0;
    lines.forEach(function (l) {
      full  += l.qty * l.price;
      final += l.qty * (groupEligible ? discountedUnit(l.price, groupPct) : l.price);
    });
    Object.keys(locPrices).forEach(function (loc) {
      var price = locPrices[loc];
      var pct = tierPct(pageQty + ((cart.prints && cart.prints[loc]) || 0));
      full  += pageQty * price;
      final += pageQty * discountedUnit(price, pct);
    });

    return {
      pageQty: pageQty,
      groupQty: groupQty,
      groupPct: groupPct,
      unitsPerShirt: unitsPerShirt,
      full: full,
      final: final,
      saving: full - final
    };
  }
  window.ftObComputePricing = computePricing;

  function hideThemeVariantPickers() {
    document.body.classList.add('size-qty-matrix-hide-theme-pickers');
    var inputs = document.querySelectorAll('input[name^="options["], select[name^="options["]');
    inputs.forEach(function (el) {
      var wrap = el.closest('fieldset, .product-form__input, .variant-picker__option, .product-form__option, .variant-input-wrap, label.product-form__label, .form__field-wrap');
      if (wrap) wrap.style.display = 'none';
      else el.style.display = 'none';
    });
  }

  function initOrderBuilder(root) {
    var allVariants = JSON.parse(root.getAttribute('data-variants'));
    var colorIndex = parseInt(root.getAttribute('data-color-index'), 10);
    var sizeIndex = parseInt(root.getAttribute('data-size-index'), 10);
    var priceMin = parseInt(root.getAttribute('data-price-min'), 10);
    var initialVariantId = parseInt(root.getAttribute('data-initial-variant-id'), 10);
    // Whether THIS product's garments count in Hulk's "combined discounts"
    // (Printable Gear) pool. Set by the Liquid snippet from the same rules
    // the collection uses. Defaults to true if the attribute is missing,
    // since every order-builder-ready product so far is Printable.
    var groupEligible = root.getAttribute('data-volume-group') !== 'false';

    var variantId = initialVariantId;
    var selectedColor = '';
    if (colorIndex >= 0) {
      var initial = allVariants.find(function (v) { return v.id === initialVariantId; }) || allVariants[0];
      if (initial && initial.options) selectedColor = initial.options[colorIndex] || '';
    }

    var swatchesEl = root.querySelector('[data-ob-swatches]');
    var tbody = root.querySelector('[data-ob-body]');
    var summaryEl = root.querySelector('[data-ob-summary]');
    var totalQtyEl = root.querySelector('[data-ob-total-qty]');
    var discountEl = root.querySelector('[data-ob-discount]');
    var totalPriceEl = root.querySelector('[data-ob-total-price]');
    var fullPriceEl = root.querySelector('[data-ob-full-price]');
    var perShirtEl = root.querySelector('[data-ob-per-shirt]');
    var tierPromptEl = root.querySelector('[data-ob-tier-prompt]');
    var locationsEl = root.querySelector('[data-ob-locations]');
    var messageEl = root.querySelector('[data-ob-message]');

    var printExtra = 0; // dollars, per-shirt, sum of selected location prices
    var selectedLocations = {}; // key -> true

    // Cart-wide quantity awareness (2026-09-03 — real customer complaint:
    // ordered 10 shirts, 2 hoodies, 1 tank in one visit, called the site
    // "too complicated" / "not working"). The bulk tiers are enforced
    // cart-wide at checkout, so the preview must count what's already in
    // the cart too.
    //
    // 2026-10-15: bucketed the way Hulk actually counts — `group` is every
    // cart item in the Printable Gear pool (Printable-tagged garments),
    // `prints[loc]` is each print add-on's own quantity. The old single count included non-Printable items
    // (marketplace tees, the rush-order fee line, etc.), which could show
    // a tier the cart would never apply.
    var cartCounts = { group: 0, prints: {} };
    var printLocByVariant = {};
    Object.keys(PRINT_VARIANTS).forEach(function (loc) { printLocByVariant[PRINT_VARIANTS[loc]] = loc; });

    function isVolumeGroupProduct(p) {
      if (!p) return false;
      var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var type = String(p.type || '');
      return tags.indexOf('printable') !== -1 &&
        type !== 'mws_apo_generated' &&
        type.indexOf('mws_fee_generated') === -1;
    }

    function fetchCartCounts() {
      fetch('/cart.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var prints = {};
          var byHandle = {};
          cart.items.forEach(function (item) {
            var loc = printLocByVariant[item.variant_id];
            if (loc) {
              prints[loc] = (prints[loc] || 0) + item.quantity;
              return;
            }
            if (!item.handle) return;
            byHandle[item.handle] = (byHandle[item.handle] || 0) + item.quantity;
          });
          cartCounts.prints = prints;
          updateSummary();

          var handles = Object.keys(byHandle);
          if (!handles.length) return;
          return Promise.all(handles.map(function (h) {
            return fetch('/products/' + encodeURIComponent(h) + '.js')
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (p) { return isVolumeGroupProduct(p) ? byHandle[h] : 0; })
              .catch(function () { return 0; });
          })).then(function (counts) {
            cartCounts.group = counts.reduce(function (a, b) { return a + b; }, 0);
            updateSummary();
          });
        })
        .catch(function () { /* cart-awareness is a nice-to-have — never block the page on it */ });
    }
    fetchCartCounts();

    // Zone-thumbnail config (garment photo + print-area rect per side),
    // fetched independently of ft-mockup-builder.js so the pills' visuals
    // don't depend on that widget's own (async, and on some products
    // absent) init. Pills render immediately with text only; thumbnails
    // fill in a moment later once this resolves.
    var zoneCfg = null;
    var configUrl = root.getAttribute('data-config-url');
    var productKey = root.getAttribute('data-product-key');
    if (configUrl && productKey) {
      fetch(configUrl).then(function (r) { return r.json(); }).then(function (json) {
        zoneCfg = json.products && json.products[productKey];
        if (zoneCfg) { renderSwatches(); renderLocationPills(); recomputeSelectedLocations(); }
      }).catch(function () { /* swatch colours/thumbnails are a nice-to-have, not required */ });
    }

    function colourIdForLabel(label) {
      if (!zoneCfg || !zoneCfg.colours) return null;
      var lower = (label || '').toLowerCase();
      var match = zoneCfg.colours.filter(function (c) { return c.label.toLowerCase() === lower; })[0];
      return match ? match.id : null;
    }

    // Real sampled hex per colour already exists in ft-print-zones.json
    // (used today only for the mockup canvas/pill thumbnails) — reusing it
    // here turns the colour picker from plain text buttons into actual
    // colour swatches, no new data needed.
    function hexForLabel(label) {
      if (!zoneCfg || !zoneCfg.colours) return null;
      var lower = (label || '').toLowerCase();
      var match = zoneCfg.colours.filter(function (c) { return c.label.toLowerCase() === lower; })[0];
      return match ? match.hex : null;
    }

    hideThemeVariantPickers();

    function getUniqueColors() {
      var seen = {};
      var out = [];
      allVariants.forEach(function (v) {
        var c = v.options && v.options[colorIndex];
        if (c && !seen[c]) { seen[c] = true; out.push(c); }
      });
      return out;
    }

    function colorIsAvailable(color) {
      return allVariants.some(function (v) { return v.options && v.options[colorIndex] === color && v.available; });
    }

    function findVariantForColor(color) {
      if (colorIndex < 0) return allVariants[0];
      return allVariants.find(function (v) { return v.options && v.options[colorIndex] === color && v.available; })
        || allVariants.find(function (v) { return v.options && v.options[colorIndex] === color; })
        || allVariants[0];
    }

    function getVariantsForColor(color) {
      if (colorIndex < 0) return allVariants;
      return allVariants.filter(function (v) { return v.options && v.options[colorIndex] === color; });
    }

    // Most products offer 15-20+ colours, but real sales data is heavily
    // concentrated in the first few (on the AS Colour Staple Tee, Black
    // alone is ~95% of a year of sales, with Charcoal/White/Cobalt/Petrol
    // Blue a distant rest — Roland's 2026-09-04 usability pass). Rather
    // than overwhelm every visitor with the full palette up front, show
    // only the first SWATCH_VISIBLE_LIMIT (the admin-configured colour
    // order already puts the actual best-sellers first) and let a "See all"
    // toggle reveal the rest for anyone who wants a less common colour.
    var SWATCH_VISIBLE_LIMIT = 6;
    var swatchesExpanded = window.location.pathname.indexOf('/products/wos-maple-tee-4001') !== -1;

    function renderSwatches() {
      if (!swatchesEl || colorIndex < 0) return;
      swatchesEl.innerHTML = '';
      var colors = getUniqueColors();
      var showToggle = colors.length > SWATCH_VISIBLE_LIMIT;

      colors.forEach(function (color, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'size-qty-matrix__swatch';
        btn.setAttribute('data-color', color);
        if (!colorIsAvailable(color)) btn.classList.add('is-unavailable');
        if (color === selectedColor) btn.classList.add('is-selected');
        // Always show the currently-selected colour even if it's past the
        // cutoff (e.g. arrived via a link/reorder that picked a less common
        // colour) — collapsing it out of view would look like the pick
        // silently reverted.
        if (showToggle && !swatchesExpanded && i >= SWATCH_VISIBLE_LIMIT && color !== selectedColor) {
          btn.hidden = true;
        }

        var hex = hexForLabel(color);
        if (hex) {
          btn.classList.add('has-chip');
          var chip = document.createElement('span');
          chip.className = 'size-qty-matrix__swatch-chip';
          chip.style.background = hex;
          btn.appendChild(chip);
        }
        var text = document.createElement('span');
        text.className = 'size-qty-matrix__swatch-text';
        text.textContent = color;
        btn.appendChild(text);

        btn.addEventListener('click', function () { selectColor(color); });
        swatchesEl.appendChild(btn);
      });

      if (showToggle) {
        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'size-qty-matrix__swatch-toggle';
        toggle.textContent = swatchesExpanded ? 'Show fewer colours' : 'See all ' + colors.length + ' colours';
        toggle.addEventListener('click', function () {
          swatchesExpanded = !swatchesExpanded;
          renderSwatches();
        });
        swatchesEl.appendChild(toggle);
      }
    }

    function updateSwatchUI() {
      if (!swatchesEl) return;
      swatchesEl.querySelectorAll('.size-qty-matrix__swatch').forEach(function (btn) {
        btn.classList.toggle('is-selected', btn.getAttribute('data-color') === selectedColor);
      });
    }

    function selectColor(color) {
      if (color === selectedColor) return;
      selectedColor = color;
      var variant = findVariantForColor(color);
      if (variant) variantId = variant.id;
      updateSwatchUI();
      buildMatrix(color);
      updatePillThumbColours();

      var idInput = document.querySelector('input[name="id"]');
      if (idInput && variant) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var nativeInputs = document.querySelectorAll('input[data-variant-input]');
      for (var ni = 0; ni < nativeInputs.length; ni++) {
        var nativeName = (nativeInputs[ni].name || '').toLowerCase();
        if ((nativeName === 'color' || nativeName === 'colour') && nativeInputs[ni].value === color) {
          if (!nativeInputs[ni].checked) {
            nativeInputs[ni].checked = true;
            nativeInputs[ni].dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    }

    function getTotalQty() {
      var total = 0;
      tbody.querySelectorAll('.ft-ob__qty-input').forEach(function (input) {
        total += parseInt(input.value, 10) || 0;
      });
      return total;
    }

    function buildMatrix(color) {
      var variants = getVariantsForColor(color);
      tbody.innerHTML = '';
      variants.forEach(function (variant) {
        var tr = document.createElement('tr');
        if (!variant.available) tr.classList.add('is-unavailable');
        var sizeLabel = sizeIndex >= 0 && variant.options ? variant.options[sizeIndex] : variant.title;
        var priceSuffix = '';
        if (variant.price > priceMin) {
          priceSuffix = ' <span class="ft-ob__price-note">(+' + formatMoney(variant.price - priceMin) + ')</span>';
        }
        tr.innerHTML =
          '<td>' + sizeLabel + (!variant.available ? ' <span class="ft-ob__soldout">(sold out)</span>' : '') + '</td>' +
          '<td>' + formatMoney(variant.price) + priceSuffix + '</td>' +
          '<td><input type="number" class="ft-ob__qty-input" data-variant-id="' + variant.id + '" data-variant-price="' + variant.price + '" min="0" max="999" value="0"' + (variant.available ? '' : ' disabled') + '></td>';
        tbody.appendChild(tr);
      });
      tbody.querySelectorAll('.ft-ob__qty-input').forEach(function (input) {
        input.addEventListener('input', updateSummary);
        input.addEventListener('change', updateSummary);
      });
      updateSummary();
    }

    function anyLocationSelected() {
      return Object.keys(selectedLocations).some(function (loc) { return selectedLocations[loc]; });
    }

    // Per-location price in cents, read straight off the real
    // print-location-selector.liquid markup — single source of truth, so a
    // price change there flows through here with nothing to keep in sync.
    function locationPriceCents(loc) {
      var el = document.querySelector('.pls-option[data-location="' + loc + '"][data-price]');
      if (!el) return null;
      var p = parseFloat(el.getAttribute('data-price'));
      return isNaN(p) ? null : Math.round(p * 100);
    }

    function updateSummary() {
      var lines = [];
      tbody.querySelectorAll('.ft-ob__qty-input').forEach(function (input) {
        var qty = parseInt(input.value, 10) || 0;
        if (qty < 0) { input.value = 0; qty = 0; }
        if (qty > 0) lines.push({ qty: qty, price: parseInt(input.getAttribute('data-variant-price'), 10) });
      });

      var locPrices = {};
      Object.keys(selectedLocations).forEach(function (loc) {
        if (!selectedLocations[loc]) return;
        var c = locationPriceCents(loc);
        if (c !== null) locPrices[loc] = c;
      });

      var pricing = computePricing(lines, locPrices, groupEligible, cartCounts);
      var totalQty = pricing.pageQty;

      // A garment-only total (no print location picked yet) isn't the
      // number a customer actually cares about — it's not the price of
      // anything they can order (every Printable product needs at least
      // one print location) and just reads as a premature/wrong-looking
      // total. Wait for a real location before showing anything
      // (2026-09-03, Roland's feedback).
      if (totalQty > 0 && anyLocationSelected()) {
        summaryEl.hidden = false;
        totalQtyEl.textContent = totalQty;

        var tier = getDiscount(pricing.groupQty);
        if (pricing.saving > 0 && tier) {
          discountEl.textContent = tier.label + ' bulk price';
          discountEl.hidden = false;
        } else {
          discountEl.hidden = true;
        }

        totalPriceEl.textContent = formatMoney(pricing.final);
        if (fullPriceEl) {
          if (pricing.saving > 0) {
            fullPriceEl.textContent = formatMoney(pricing.full);
            fullPriceEl.hidden = false;
          } else {
            fullPriceEl.hidden = true;
          }
        }
        // Effective per-shirt price (garment + all its prints, bulk
        // discount folded in) — the number a customer ordering 10+
        // actually thinks in. Skipped at qty 1.
        if (perShirtEl) {
          perShirtEl.textContent = totalQty > 1 ? ('(' + formatMoney(Math.round(pricing.final / totalQty)) + '/shirt)') : '';
        }

        if (tierPromptEl) {
          var cartNote = cartCounts.group > 0
            ? ' <span class="ft-ob__tier-cart-note">(' + cartCounts.group + ' already in your cart, counted in)</span>'
            : '';
          var nextTier = getNextTier(pricing.groupQty);
          var shirtsNeeded = (nextTier && pricing.unitsPerShirt > 0)
            ? Math.ceil((nextTier.min - pricing.groupQty) / pricing.unitsPerShirt)
            : 0;
          var msg = '';

          if (pricing.saving > 0) {
            msg = 'Bulk pricing applied — you save <strong>' + formatMoney(pricing.saving) + '</strong>.';
            if (nextTier && shirtsNeeded > 0) {
              msg += ' Add <strong>' + shirtsNeeded + ' more</strong> to reach <strong>' + nextTier.label + '</strong>.';
            } else if (!nextTier) {
              msg += ' That’s our best price.';
            }
          } else if (nextTier && shirtsNeeded > 0 && pricing.groupQty >= nextTier.min / 2) {
            // Same defer-until-close rule as before — keeps "add 9 more"
            // from showing the instant someone types qty 1.
            msg = 'Add <strong>' + shirtsNeeded + ' more</strong> — here or on another product — to unlock <strong>' + nextTier.label + '</strong>.';
          }

          if (msg) {
            tierPromptEl.innerHTML = msg + cartNote + ' <span class="ft-ob__tier-cart-note">Shipping calculated at checkout.</span>';
            tierPromptEl.hidden = false;
          } else {
            tierPromptEl.hidden = true;
          }
        }

      } else {
        summaryEl.hidden = true;
        if (tierPromptEl) tierPromptEl.hidden = true;
      }

      updateAddBtnState(totalQty);
    }

    // ---------- Print locations ----------
    // Builds the small garment-thumbnail + dashed zone-rect visual for a
    // pill — same rect-as-percent-of-canvas math ft-mockup-builder.js's
    // buildZoneVisualBlock() uses, kept independent here (see zoneCfg
    // fetch above) rather than reaching into that widget.
    // F7 (mobile audit, 6 Sep 2026): the pill thumbnails render at 74px but
    // were requesting the full 1896px template on first paint AND on every
    // colour change (~200 KB per colour click on a phone). Same CDN-resize
    // trick as ft-mockup-builder.js's ftCdnWidth(): Shopify serves ?width=N
    // as an N-wide WebP. Display-only — nothing here feeds a canvas or the
    // production snapshot, and the mockup builder keeps its own unsized URLs.
    function ftObCdnWidth(url, w) {
      if (!url || !w) return url;
      if (/^(data:|blob:)/i.test(url)) return url;
      if (/[?&]width=/.test(url)) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }
    function ftObCdnSrcset(url, widths) {
      if (!url || /^(data:|blob:)/i.test(url)) return '';
      return widths.map(function (w) { return ftObCdnWidth(url, w) + ' ' + w + 'w'; }).join(', ');
    }
    function ftObSizeThumb(img, url) {
      img.src = ftObCdnWidth(url, 200);
      var ss = ftObCdnSrcset(url, [200, 400]);
      if (ss) { img.setAttribute('srcset', ss); img.setAttribute('sizes', '74px'); }
      else { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
      img.decoding = 'async';
    }

    function buildPillThumb(location) {
      var mapping = LOCATION_TO_ZONE[location];
      if (!mapping || !zoneCfg) return null;
      var sideCfg = zoneCfg.sides && zoneCfg.sides[mapping.side];
      var zone = sideCfg && sideCfg.zones && sideCfg.zones[mapping.zone];
      if (!sideCfg || !zone) return null;

      var colourId = colourIdForLabel(selectedColor);
      var tplUrl = (colourId && sideCfg.templates[colourId]) || sideCfg.templates._default;
      if (!tplUrl) return null;

      var canvasW = zoneCfg.canvas.width;
      var canvasH = zoneCfg.canvas.height;

      var thumb = document.createElement('span');
      thumb.className = 'ft-ob__pill-thumb';
      var img = document.createElement('img');
      ftObSizeThumb(img, tplUrl);
      img.alt = '';
      img.setAttribute('data-pill-thumb-side', mapping.side);
      thumb.appendChild(img);

      var box = document.createElement('span');
      box.className = 'ft-ob__pill-zone';
      box.style.left = (zone.rect.x / canvasW * 100) + '%';
      box.style.top = (zone.rect.y / canvasH * 100) + '%';
      box.style.width = (zone.rect.width / canvasW * 100) + '%';
      box.style.height = (zone.rect.height / canvasH * 100) + '%';
      thumb.appendChild(box);

      return thumb;
    }

    // Re-points every pill thumbnail at the newly-selected colour's
    // template — called from selectColor() so switching colour keeps the
    // garment shown in the pills consistent with the rest of the widget,
    // same pattern as ft-mockup-builder.js's updateLocationChoiceThumbs().
    function updatePillThumbColours() {
      if (!zoneCfg) return;
      locationsEl.querySelectorAll('img[data-pill-thumb-side]').forEach(function (img) {
        var sideId = img.getAttribute('data-pill-thumb-side');
        var sideCfg = zoneCfg.sides && zoneCfg.sides[sideId];
        if (!sideCfg) return;
        var colourId = colourIdForLabel(selectedColor);
        ftObSizeThumb(img, (colourId && sideCfg.templates[colourId]) || sideCfg.templates._default);
      });
    }

    function renderLocationPills() {
      var options = document.querySelectorAll('.pls-option[data-location][data-price]');
      locationsEl.innerHTML = '';
      options.forEach(function (el) {
        var location = el.getAttribute('data-location');
        var price = parseFloat(el.getAttribute('data-price'));
        var titleEl = el.querySelector('.pls-option-title') || el.querySelector('.pls-promo-locked-text strong');
        var label = titleEl ? titleEl.textContent.trim() : location;
        var checkbox = document.getElementById(PLS_CHECKBOX_IDS[location]);
        if (!checkbox) return;

        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'ft-ob__pill';
        pill.setAttribute('data-location', location);
        if (checkbox.checked) pill.classList.add('is-selected');

        var thumb = buildPillThumb(location);
        if (thumb) { pill.classList.add('has-thumb'); pill.appendChild(thumb); }

        var text = document.createElement('span');
        text.className = 'ft-ob__pill-text';
        text.textContent = label + ' (+' + formatMoney(Math.round(price * 100)) + ')';
        pill.appendChild(text);

        // Design-status sub-label ("Needs a design" / "✓ Design added")
        // is owned entirely by recomputeSelectedLocations() below, not
        // here — that's the function that actually runs on every
        // selection change (via the printlocation:pricechange listener),
        // while THIS function only runs once at init plus once more if
        // the zone-config fetch resolves late, always before the
        // customer has selected anything. Building the status span here,
        // gated on checkbox.checked, meant it could only ever be created
        // in a state where checked was false — so it silently never
        // existed for any pill a customer actually selected (confirmed
        // live, 2026-09-04, cost real debugging time to track down).

        pill.addEventListener('click', function () {
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          // If the customer used the top-of-page "design it with AI
          // first" entry point, there's a design waiting to be attached —
          // apply it to THIS location the instant they pick it, rather
          // than silently waiting until they click all the way through to
          // the design/upload step (2026-09-04, Roland's feedback: not
          // clear the design would actually get used). No-ops harmlessly
          // if there's no pending design, or this location already has
          // real art attached.
          if (checkbox.checked) {
            var mbRoot = document.querySelector('[data-ft-mockup-builder]');
            var mapping = LOCATION_TO_ZONE[location];
            if (mbRoot && mbRoot.__ftApplyPendingAiDesign && mapping) {
              // Visible feedback right where the customer's eyes already
              // are — the pill they just pressed — on top of the top-of-
              // page banner's own "Attaching…" state. This attach is a
              // real network round trip (a few seconds, longer if the
              // print-quality upgrade has to fail over), and with nothing
              // changing on screen during that window a customer could
              // reasonably think the click didn't register (2026-09-04,
              // Roland's usability audit).
              pill.classList.add('is-applying-design');
              mbRoot.__ftApplyPendingAiDesign(mapping.side).finally(function () {
                pill.classList.remove('is-applying-design');
                // Re-scans every selected pill's real hasArt() state and
                // creates/updates/removes each one's status span as
                // needed — same function driving the badge on every other
                // selection change, so this attach's completion is just
                // one more trigger of the one real code path instead of a
                // separate one-off update.
                recomputeSelectedLocations();
              });
            }
          }
        });

        locationsEl.appendChild(pill);
      });
      recomputeSelectedLocations();
    }

    // The one function that actually runs on every selection change (the
    // printlocation:pricechange listener below calls this on every
    // checkbox change, not just at init) — so it's also the right place
    // to own each pill's "Needs a design" / "✓ Design added" status
    // span: create it the first time a location's selected, update it
    // whenever hasArt() changes (e.g. once a pending AI design finishes
    // attaching), remove it if the location gets deselected.
    function recomputeSelectedLocations() {
      selectedLocations = {};
      Object.keys(PLS_CHECKBOX_IDS).forEach(function (loc) {
        var checkbox = document.getElementById(PLS_CHECKBOX_IDS[loc]);
        selectedLocations[loc] = !!(checkbox && checkbox.checked);
      });
      var mbRoot = document.querySelector('[data-ft-mockup-builder]');
      var controller = mbRoot && mbRoot.__ftMockupController;
      locationsEl.querySelectorAll('.ft-ob__pill').forEach(function (pill) {
        var loc = pill.getAttribute('data-location');
        var isSelected = !!selectedLocations[loc];
        pill.classList.toggle('is-selected', isSelected);

        var statusEl = pill.querySelector('.ft-ob__pill-status');
        var mapping = LOCATION_TO_ZONE[loc];
        if (isSelected && mapping) {
          if (!statusEl) {
            statusEl = document.createElement('span');
            statusEl.className = 'ft-ob__pill-status';
            pill.appendChild(statusEl);
          }
          var hasArtNow = !!(controller && controller.hasArt(mapping.side));
          var reorderToggle = mbRoot && mbRoot.querySelector('[data-reorder-toggle]');
          var reordering = !!(reorderToggle && reorderToggle.checked);
          statusEl.textContent = reordering ? 'Previous design' : (hasArtNow ? '✓ Design added' : 'Needs a design');
          statusEl.classList.toggle('is-done', reordering || hasArtNow);
        } else if (statusEl) {
          statusEl.remove();
        }
      });
    }

    document.addEventListener('change', function (event) {
      if (event.target.matches('[data-reorder-toggle]')) recomputeSelectedLocations();
    });

    // Refresh artwork badges when returning from the final review.
    document.addEventListener('ft:edit-garments', recomputeSelectedLocations);

    document.addEventListener('printlocation:pricechange', function (e) {
      printExtra = e.detail.extra || 0;
      recomputeSelectedLocations();
      updateSummary();
    });

    // ---------- Cart ----------
    function getProperties() {
      var form = root.closest('form[action*="/cart/add"]') || document;
      var props = {};
      form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(function (input) {
        var match = input.name.match(/^properties\[(.+)\]$/);
        if (match && input.value) props[match[1]] = input.value;
      });
      return props;
    }

    function buildBaseItems(properties, totalQty) {
      var items = [];
      var groupId = 'ft-' + (window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));
      properties._ft_order_group = groupId;
      properties._ft_print_locations = JSON.stringify(Object.keys(selectedLocations).filter(function(k){return selectedLocations[k];}));
      // Preserve operational metadata privately; keep the customer summary short.
      ['Copyright Confirmation','Preview Colour','Print Locations','Design review needed'].forEach(function(k){if(properties[k]){properties['_'+k]=properties[k];delete properties[k];}});
      Object.keys(properties).forEach(function(k){if(/print file/i.test(k)&&k.charAt(0)!=='_'){properties['_'+k]=properties[k];delete properties[k];}});
      tbody.querySelectorAll('.ft-ob__qty-input').forEach(function (input) {
        var qty = parseInt(input.value, 10) || 0;
        if (qty > 0) {
          items.push({ id: parseInt(input.getAttribute('data-variant-id'), 10), quantity: qty, properties: properties });
        }
      });
      if (totalQty > 0) {
        ['front', 'large_front', 'back', 'left_sleeve', 'right_sleeve'].forEach(function (loc) {
          if (selectedLocations[loc]) {
            items.push({
              id: PRINT_VARIANTS[loc],
              quantity: totalQty,
              properties: { '_ft_order_group': groupId, '_ft_print_type': loc, '_Order reference': properties['T-Shirt Print Location'] || loc }
            });
          }
        });
      }
      return items;
    }

    function showMessage(text) {
      if (!messageEl) return;
      messageEl.textContent = text;
      messageEl.hidden = false;
      setTimeout(function () { messageEl.hidden = true; }, 5000);
    }

    async function sendItemsToCart(items) {
      if (!items.length) return;
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.dataset.origText = addBtn.dataset.origText || addBtn.textContent;
        addBtn.textContent = 'Adding to cart…';
      }
      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!response.ok) {
          var err = await response.json();
          showMessage(err.description || 'Error adding to cart');
          if (addBtn) { addBtn.textContent = addBtn.dataset.origText; updateAddBtnState(getTotalQty()); }
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        showMessage('Network error — please try again');
        if (addBtn) { addBtn.textContent = addBtn.dataset.origText; updateAddBtnState(getTotalQty()); }
      }
    }

    async function addToCart() {
      var properties = getProperties();
      var totalQty = getTotalQty();
      var items = buildBaseItems(properties, totalQty);
      if (!items.length) return;
      await sendItemsToCart(items);
    }

    var addBtn = document.getElementById('pls-main-add-btn');
    var hasWizard = !!document.querySelector('[data-ft-mockup-builder]');
    function updateAddBtnState(totalQty) {
      if (!addBtn) return;
      // When the mockup wizard is present it owns this button's disabled
      // state (syncFormProperties -> blocked, which correctly requires
      // real uploaded artwork per chosen print location before allowing
      // checkout, on top of its own copyright-confirmation gate) — a
      // simpler qty-only check here would race it and sometimes win,
      // letting the customer add to cart before they've actually designed
      // anything. Only take control of enablement ourselves when there's
      // no wizard on the page at all.
      if (hasWizard) return;
      addBtn.disabled = !(totalQty > 0);
    }
    if (addBtn) addBtn.addEventListener('click', addToCart);

    // Safety net: #pls-main-add-btn is a plain <button type="button">
    // specifically so it never natively submits — real submissions are
    // meant to go through addToCart() above. But the underlying <form
    // action="/cart/add"> is still a real, submittable form, and anything
    // that triggers a native submit bypasses addToCart() (and its
    // getProperties()/buildBaseItems() logic) entirely, hitting Shopify's
    // cart-add with next to nothing attached. Same fix applied to
    // snippets/size-quantity-matrix.liquid 2026-08-12 after real "Special"
    // package-deal orders came through with quantity 1 and zero custom
    // attributes — this widget shares the identical pattern (same shared
    // button, same non-submit type), so it carries the same structural
    // risk even though it hasn't been observed here yet.
    if (addBtn) {
      var pageForm = addBtn.closest('form[action*="/cart/add"]');
      if (pageForm) {
        pageForm.addEventListener('submit', function (e) {
          e.preventDefault();
          if (!addBtn.disabled) {
            // Deferred, not called inline — clicking synchronously from
            // within a 'submit' handler was observed (real testing,
            // 2026-08-12) to silently swallow the click on the sibling
            // widget's identical button/form combination.
            setTimeout(function () { addBtn.click(); }, 0);
          }
        }, true);
      }
    }

    renderSwatches();
    buildMatrix(selectedColor);
    renderLocationPills();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-ft-order-builder]').forEach(initOrderBuilder);
    // print-location-selector.liquid's own script may run after this one in
    // document order (both `defer`, execution order matches document
    // order) but its checkboxes are already in the DOM by DOMContentLoaded
    // regardless — renderLocationPills() above reads them synchronously.
  });
})();